import { z } from "zod";

// ---------------------------------------------------------------------------
// Business memory
//
// A persisted, evolving knowledge store per business at:
//   Businesses/{businessId}/memory/{memoryId}
//
// Memory is the "shared brain" behind the insights engine: durable facts the
// AI accumulates (and humans curate) about how a business and its teams work.
// It is distinct from `insight.ts` — insights are ephemeral surfaced cards;
// memory entries are long-lived facts that get re-injected into every nightly
// generation run and into the live Intelligence chat.
//
// Lifecycle: the nightly reflection step lets the AI add/update/confirm/archive
// entries; a deterministic decay pass then archives stale, unconfirmed AI
// entries. Human entries are pinned ground truth the AI can never overwrite.
// ---------------------------------------------------------------------------

export const MemoryScope = z.enum(["business", "team", "member"]);
export type MemoryScope = z.infer<typeof MemoryScope>;

export const MemorySource = z.enum(["ai", "human"]);
export type MemorySource = z.infer<typeof MemorySource>;

export const MemoryStatus = z.enum(["active", "archived"]);
export type MemoryStatus = z.infer<typeof MemoryStatus>;

export const MemoryCategory = z.enum([
  "culture", // how the team works, values, norms
  "context", // business situation, seasonality, market
  "people", // member-specific durable facts
  "goal", // stated objectives, targets
  "pattern", // observed recurring dynamics
  "event", // notable changes (promotion, reorg, churn)
  // Which system holds which kind of data, as an admin stated it ("Garba
  // records the calls, Upsales is our CRM"). Read by the CRM agent ahead of
  // the resolved default (docs/ai-platform/09, data sources revised).
  "datasource",
]);
export type MemoryCategory = z.infer<typeof MemoryCategory>;

// ---------------------------------------------------------------------------
// Firestore document shape
// ---------------------------------------------------------------------------

export interface MemoryEntry {
  id: string;
  businessId: string;
  scope: MemoryScope;
  salesTeamId: string | null;
  memberId: string | null;
  category: MemoryCategory;
  /** One concise, durable fact. */
  text: string;
  source: MemorySource;
  confidence: number;
  /** Human-pinned entries are ground truth — never auto-archived or AI-edited. */
  pinned: boolean;
  status: MemoryStatus;
  evidence: string[];
  /** uid for human entries, generatorKey for AI entries. */
  createdBy: string | null;
  modelId: string | null;
  promptVersion: number | null;
  /** Last run that re-affirmed this entry (drives decay). */
  lastConfirmedAt: string | null;
  /** Memory id that replaced this one, if superseded. */
  supersededBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

// ---------------------------------------------------------------------------
// Reflection output — the AI's memory edits per nightly run
// ---------------------------------------------------------------------------

export const MEMORY_TEXT_MAX = 280;

/**
 * Model-emitted evidence lists truncate instead of rejecting: a schema
 * `.max()` would fail the entire reflection batch over one extra element
 * (same rationale as {@link MEMORY_MAX_OPS_PER_RUN}). The `.describe()` hint
 * keeps steering the model since the JSON schema loses `maxItems`.
 */
const MEMORY_EVIDENCE_MAX = 4;

/**
 * The model-facing ops clamp `text`/`reason` with a transform rather than
 * `.max()`, for the same reason MEMORY_MAX_OPS_PER_RUN is a slice: a hard max
 * rejects the WHOLE batch. One 301-char entry discarded a 17-operation
 * reflection in production, and `runAgentStructured` reports it as the opaque
 * "No object generated: response did not match schema". The prompt still asks
 * for <=280. The tRPC inputs below keep `.max()` on purpose - a human typing
 * past the limit should get an error, not silent truncation.
 */
const MemoryAddOp = z.object({
  action: z.literal("add"),
  category: MemoryCategory,
  scope: MemoryScope.default("business"),
  memberId: z.string().nullable().default(null),
  salesTeamId: z.string().nullable().default(null),
  text: z
    .string()
    .min(1)
    .describe(`Max ${MEMORY_TEXT_MAX} tecken`)
    .transform((t) => t.slice(0, MEMORY_TEXT_MAX)),
  confidence: z.number().min(0).max(1),
  evidence: z
    .array(z.string())
    .describe(`Max ${MEMORY_EVIDENCE_MAX} korta punkter`)
    .default([])
    .transform((a) => a.slice(0, MEMORY_EVIDENCE_MAX)),
});

const MemoryUpdateOp = z.object({
  action: z.literal("update"),
  id: z.string().min(1),
  text: z
    .string()
    .min(1)
    .describe(`Max ${MEMORY_TEXT_MAX} tecken`)
    .nullable()
    .default(null)
    .transform((t) => (t ? t.slice(0, MEMORY_TEXT_MAX) : t)),
  confidence: z.number().min(0).max(1).nullable().default(null),
  evidence: z
    .array(z.string())
    .describe(`Max ${MEMORY_EVIDENCE_MAX} korta punkter`)
    .nullable()
    .default(null)
    .transform((a) => (a ? a.slice(0, MEMORY_EVIDENCE_MAX) : a)),
});

const MemoryConfirmOp = z.object({
  action: z.literal("confirm"),
  id: z.string().min(1),
});

const MemoryArchiveOp = z.object({
  action: z.literal("archive"),
  id: z.string().min(1),
  reason: z
    .string()
    .min(1)
    .describe(`Max ${MEMORY_TEXT_MAX} tecken`)
    .transform((r) => r.slice(0, MEMORY_TEXT_MAX)),
});

export const MemoryOp = z.discriminatedUnion("action", [
  MemoryAddOp,
  MemoryUpdateOp,
  MemoryConfirmOp,
  MemoryArchiveOp,
]);
export type MemoryOp = z.infer<typeof MemoryOp>;

/**
 * Operational ceiling on ops applied per reflection run. Enforced as a
 * defensive slice in the reflection step — NOT a schema `.max()`, which would
 * reject the entire batch on overflow (a cold-start with rich data legitimately
 * produces many ops). The model lists cross-cutting facts first, so slicing
 * keeps the highest-value ones. The prompt steers it to a much smaller target.
 */
export const MEMORY_MAX_OPS_PER_RUN = 25;

export const ReflectionBatch = z.object({
  operations: z.array(MemoryOp).default([]),
});
export type ReflectionBatch = z.infer<typeof ReflectionBatch>;

// ---------------------------------------------------------------------------
// Lifecycle tuning
// ---------------------------------------------------------------------------

/** Per-run confidence decrement for active AI entries not confirmed/updated. */
export const MEMORY_DECAY_STEP = 0.1;
/** Below this confidence, an unpinned AI entry is archived by the decay pass. */
export const MEMORY_ARCHIVE_THRESHOLD = 0.2;
/** Confidence bump applied when the AI re-confirms an existing entry. */
export const MEMORY_CONFIRM_BOOST = 0.1;
/** Soft cap on active entries kept per business (by pinned → confidence → recency). */
export const MEMORY_ACTIVE_CAP = 50;

/**
 * Human ground truth the AI must never overwrite or auto-archive: pinned
 * entries and anything authored by a human. Pure predicate shared by the
 * reflection apply-step and the decay pass.
 */
export function isProtectedMemory(entry: {
  pinned: boolean;
  source: MemorySource;
}): boolean {
  return entry.pinned || entry.source === "human";
}

/**
 * Deterministic decay decision for an unconfirmed AI entry. Returns the next
 * confidence and whether the entry should be archived (fell below threshold).
 * Caller is responsible for skipping protected entries.
 */
export function applyMemoryDecay(confidence: number): {
  confidence: number;
  archive: boolean;
} {
  const next = confidence - MEMORY_DECAY_STEP;
  if (next < MEMORY_ARCHIVE_THRESHOLD) return { confidence: next, archive: true };
  return { confidence: Math.max(0, next), archive: false };
}

// ---------------------------------------------------------------------------
// tRPC inputs (human curation)
// ---------------------------------------------------------------------------

export const ListMemoryInput = z.object({
  businessId: z.string().min(1),
  scope: MemoryScope.optional(),
});
export type ListMemoryInput = z.infer<typeof ListMemoryInput>;

export const CreateMemoryInput = z.object({
  businessId: z.string().min(1),
  scope: MemoryScope.default("business"),
  salesTeamId: z.string().min(1).nullable().default(null),
  memberId: z.string().min(1).nullable().default(null),
  category: MemoryCategory,
  text: z.string().min(1).max(MEMORY_TEXT_MAX),
  evidence: z.array(z.string()).max(4).default([]),
});
export type CreateMemoryInput = z.infer<typeof CreateMemoryInput>;

export const UpdateMemoryInput = z.object({
  businessId: z.string().min(1),
  memoryId: z.string().min(1),
  text: z.string().min(1).max(MEMORY_TEXT_MAX).optional(),
  category: MemoryCategory.optional(),
});
export type UpdateMemoryInput = z.infer<typeof UpdateMemoryInput>;

export const ArchiveMemoryInput = z.object({
  businessId: z.string().min(1),
  memoryId: z.string().min(1),
});
export type ArchiveMemoryInput = z.infer<typeof ArchiveMemoryInput>;

export const PinMemoryInput = z.object({
  businessId: z.string().min(1),
  memoryId: z.string().min(1),
  pinned: z.boolean(),
});
export type PinMemoryInput = z.infer<typeof PinMemoryInput>;
